import React from "react";
import { Button, Col, Form, Row } from "reactstrap";
import BaseForm from "../../common/BaseForm";
import UpdatedvrModel from "../../models/remotecontrol/UpdatedvrModel";
import { fetchUpdateDvrCapabilitiesOnPrem } from "../../../services/remoteControlService";
import { hasPlaceholder } from "../../cameraPrototypes/shared/hostRange";

class UpdatedvrForm extends BaseForm {
  state = {
    data: {},
    errors: {},
    useCompression: false,
    useFps: false,
    useResolution: false,
    useBitrate: false,
    useCbrVbr: false,
    useMotionDetection: false,
    useEncryption: false,
    useLowResolution: false,
    showOtherParameters: false,
    otherParametersButtonText: "Other Parameters",
    capabilities: null,
    capabilitiesLoading: false,
    capabilitiesError: null,
    lastCapabilitiesFetchKey: null,
  };

  type_dvrs = [
    { name: "HIK", id: "hik" },
    { name: "DAHUA", id: "dahua" },
    { name: "TVT", id: "tvt" },
    { name: "UNIVIEW", id: "uniview" },
    { name: "AXIS", id: "axis" },
  ];

  type_actions = [
    { name: "Display", id: "display" },
    { name: "Update", id: "update" },
    { name: "Reboot", id: "reboot" },
  ];

  flux_types = [
    { name: "MAIN", id: "all_main" },
    { name: "SUB", id: "all_sub" },
  ];

  compression_options = [
    { name: "H.264", id: "h264" },
    { name: "H.265", id: "h265" },
  ];

  fps_options = Array.from({ length: 25 }, (_, i) => ({ name: `${i + 1}`, id: `${i + 1}` }));

  resolution_options = [
    { name: "2560x1440", id: "2560x1440" },
    { name: "1920x1080 (Full HD)", id: "1920x1080" },
    { name: "1280x720 (HD)", id: "1280x720" },
    { name: "960x576 (960H)", id: "960x576" },
    { name: "960x480 (960H)", id: "960x480" },
    { name: "704x576 (D1)", id: "704x576" },
    { name: "704x480 (D1)", id: "704x480" },
    { name: "640x360", id: "640x360" },
    { name: "640x480", id: "640x480" },
    { name: "352x288 (CIF)", id: "352x288" },
  ];

  bitrate_options = [
    { name: "256 kbps", id: "256" },
    { name: "512 kbps", id: "512" },
    { name: "768 kbps", id: "768" },
    { name: "856 kbps", id: "856" },
    { name: "1024 kbps", id: "1024" },
    { name: "1536 kbps", id: "1536" },
    { name: "2048 kbps", id: "2048" },
    { name: "2560 kbps", id: "2560" },
    { name: "3072 kbps", id: "3072" },
  ];

  cbr_vbr_options = [
    { name: "CBR", id: "cbr" },
    { name: "VBR", id: "vbr" },
  ];

  true_false_options = [
    { name: "True", id: "true" },
    { name: "False", id: "false" },
  ];

  dhcp_options = [
    { name: "Don't change", id: "" },
    { name: "Dynamic (Enable DHCP)", id: "dynamic" },
    { name: "Static (Disable DHCP)", id: "static" },
  ];

  dhcp_options_cloud = [
    { name: "Don't change", id: "" },
    { name: "Static (Disable DHCP)", id: "static" },
  ];

  updatedvr = new UpdatedvrModel();
  schema = this.updatedvr.getSchema();

  /** Returns stream capabilities for dropdowns based on flux, with fallback to static options. */
  getOptionsFromCapabilities(optionKey) {
    const { capabilities, data } = this.state;
    const { flux } = data;
    const streamCapabilities =
      flux === "all_sub" ? capabilities?.substream_capabilities : capabilities?.mainstream_capabilities;

    if (!streamCapabilities?.[optionKey]?.length) {
      return null;
    }

    const values = streamCapabilities[optionKey];
    if (optionKey === "resolution_options") {
      return values.map((v) => ({ name: v, id: v }));
    }
    return values.map((v) => ({ name: String(v), id: String(v) }));
  }

  componentDidMount() {
    const data = this.updatedvr.getDefaultData();
    const { videoRecorder } = this.props;

    if (videoRecorder) {
      // A legacy IP-camera recorder stores one templated host (`192.168.4.{}`) for a whole
      // subnet, and update-dvr wants the slot range substituted in. The camera model forbids
      // that template (CheckConstraint capturedevice_host_no_curly_placeholder), so gate on the
      // placeholder actually being there rather than on the recorder type: camera-model hosts
      // take the literal branch, legacy templated ones keep their range.
      data.ip_dvr = hasPlaceholder(videoRecorder.device_ip)
        ? String(videoRecorder.device_ip).replace(
            "{}",
            `{${(videoRecorder.channels || []).map((channel) => channel.channel_number).join(",")}}`
          )
        : videoRecorder.device_ip || "";
      data.port = "";
      data.username = videoRecorder.username || "";
      data.password = videoRecorder.password || "";
      // update-dvr dispatches on the brand, not on Simone's stream class: TVT and UNIVIEW are
      // `kind="IP"` yet have their own update-dvr support, so prefer the brand name and keep the
      // kind only as the legacy fallback.
      const dvrTypeName = (
        videoRecorder.video_recorder_brand_name || videoRecorder.video_recorder_type_name
      )?.toLowerCase();
      const matchingType = this.type_dvrs.find((type) => type.id === dvrTypeName);
      if (matchingType) {
        data.type_dvr = matchingType.id;
      }
    }

    this.setState({ data });
  }

  componentDidUpdate(prevProps, prevState) {
    const { server } = this.props;
    const { data, capabilitiesLoading, capabilities, lastCapabilitiesFetchKey } = this.state;

    const isOnPrem = server?.group_parent !== "cloud";
    const isUpdateAction = data.type_action === "update";
    const hasRequiredFields = data.ip_dvr && data.username && data.type_dvr;

    if (!isUpdateAction || !isOnPrem) {
      if (prevState.capabilities || prevState.lastCapabilitiesFetchKey) {
        this.setState({ capabilities: null, lastCapabilitiesFetchKey: null, capabilitiesError: null });
      }
      return;
    }

    if (!hasRequiredFields || capabilitiesLoading || !server?.hostname) {
      return;
    }

    const fetchKey = `${server.hostname}|${data.ip_dvr}|${data.username}|${data.password || ""}|${data.type_dvr}|${
      data.port || ""
    }|${data.flux || ""}`;
    if (capabilities && lastCapabilitiesFetchKey === fetchKey) {
      return;
    }
    if (lastCapabilitiesFetchKey === fetchKey && this.state.capabilitiesError) {
      return;
    }

    this.setState({
      capabilitiesLoading: true,
      capabilitiesError: null,
      capabilities: lastCapabilitiesFetchKey !== fetchKey ? null : capabilities,
    });
    fetchUpdateDvrCapabilitiesOnPrem(server.hostname, {
      type_dvr: data.type_dvr,
      ip_dvr: data.ip_dvr,
      username: data.username,
      password: data.password,
      port: data.port,
      flux: data.flux,
    })
      .then((result) => {
        this.setState({
          capabilities: result,
          capabilitiesLoading: false,
          lastCapabilitiesFetchKey: fetchKey,
          capabilitiesError: null,
        });
      })
      .catch((err) => {
        this.setState({
          capabilities: null,
          capabilitiesLoading: false,
          lastCapabilitiesFetchKey: fetchKey,
          capabilitiesError: err.message || "Failed to fetch capabilities",
        });
      });
  }

  handleSubmit = async () => {
    await this.props.handleSubmit(this.state.data);
  };
  handleInstallClick = async () => {
    const { data } = this.state;
    data.type_action = "install"; // Assign the action "install" to the key "type_action" in the state
    await this.setState({ data }); // update the status
    // Submit the form directly after defining the "install" action
    await this.handleSubmit();
  };
  handleCheckboxChange = (name) => (e) => {
    this.setState({ [name]: e.target.checked }, () => {
      if (name === "useLowResolution" && this.state.useLowResolution) {
        this.applyLowResolutionSettings();
      } else if (name === "useLowResolution" && !this.state.useLowResolution) {
        this.resetLowResolutionSettings();
      }
    });
  };

  handleOtherParametersClick = () => {
    this.setState((prevState) => ({
      showOtherParameters: !prevState.showOtherParameters,
      otherParametersButtonText: prevState.showOtherParameters ? "Other Parameters" : "Hide the Parameters",
      useLowResolution: prevState.showOtherParameters ? prevState.useLowResolution : false,
    }));
  };

  applyLowResolutionSettings = () => {
    const { data } = this.state;

    data.fps = "6";
    data.bitrate = "512";
    data.compression = "H.264";

    switch (data.type_dvr) {
      case "dahua":
        data.resolution = "704x576";
        break;
      case "uniview":
        data.resolution = "720x576";
        break;
      default:
        break;
    }

    this.setState({ data, showOtherParameters: false });
  };

  resetLowResolutionSettings = () => {
    const { data } = this.state;

    delete data.fps;
    delete data.bitrate;
    delete data.compression;
    delete data.resolution;

    this.setState({ data });
  };

  render() {
    const {
      data,
      useLowResolution,
      useCompression,
      useFps,
      useResolution,
      useBitrate,
      useCbrVbr,
      useMotionDetection,
      useEncryption,
      showOtherParameters,
      otherParametersButtonText,
      capabilitiesLoading,
      capabilitiesError,
    } = this.state;

    let typeActionLabel = "";
    Object.keys(this.type_actions).forEach((key) => {
      if (this.type_actions[key].id === data.type_action) {
        typeActionLabel = this.type_actions[key].name;
      }
    });

    return (
      <Form>
        <Row>
          <Col md="12">{this.renderSelect("type_action", "Select Operation", this.type_actions)}</Col>
        </Row>
        <Row>
          <Col md="4">{this.renderSelect("type_dvr", "Dvr Type", this.type_dvrs)}</Col>
          <Col md="4">{this.renderInput("ip_dvr", "Dvr IP")}</Col>
          <Col md="4">{this.renderInput("port", "API Port")}</Col>
        </Row>
        <Row>
          <Col md="4">{this.renderInput("username", "Dvr User")}</Col>
          <Col md="4">{this.renderInput("password", "Dvr Password")}</Col>
        </Row>
        {data.type_action === "update" && (
          <div>
            <Row>
              <Col md="3">{this.renderSelect("flux", "Flow", this.flux_types)}</Col>
              <Col md="3">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={useLowResolution}
                      onChange={this.handleCheckboxChange("useLowResolution")}
                    />{" "}
                    Low Resolution D1
                  </label>
                </div>
              </Col>
              <Col md="6" className="d-flex align-items-end">
                {capabilitiesLoading && (
                  <span className="text-muted">
                    <i className="fas fa-spinner fa-spin mr-1" />
                    Loading DVR capabilities...
                  </span>
                )}
                {capabilitiesError && !capabilitiesLoading && (
                  <span className="text-warning small">{capabilitiesError}</span>
                )}
              </Col>
            </Row>

            {showOtherParameters && (
              <>
                <Row>
                  <Col md="3">
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={useCompression}
                          onChange={this.handleCheckboxChange("useCompression")}
                        />{" "}
                        Video Encoding
                      </label>
                      {useCompression && this.renderSelect("compression", "", this.compression_options)}
                    </div>
                  </Col>
                  <Col md="3">
                    <div className="form-group">
                      <label>
                        <input type="checkbox" checked={useFps} onChange={this.handleCheckboxChange("useFps")} /> FPS
                      </label>
                      {useFps &&
                        this.renderSelect(
                          "fps",
                          "",
                          this.getOptionsFromCapabilities("fps_options") || this.fps_options
                        )}
                    </div>
                  </Col>
                  <Col md="3">
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={useResolution}
                          onChange={this.handleCheckboxChange("useResolution")}
                        />{" "}
                        Resolution
                      </label>
                      {useResolution &&
                        this.renderSelect(
                          "resolution",
                          "",
                          this.getOptionsFromCapabilities("resolution_options") || this.resolution_options
                        )}
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md="3">
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={useBitrate}
                          onChange={this.handleCheckboxChange("useBitrate")}
                        />{" "}
                        Bitrate
                      </label>
                      {useBitrate &&
                        this.renderSelect(
                          "bitrate",
                          "",
                          this.getOptionsFromCapabilities("bitrate_options") || this.bitrate_options
                        )}
                    </div>
                  </Col>
                  <Col md="3">
                    <div className="form-group">
                      <label>
                        <input type="checkbox" checked={useCbrVbr} onChange={this.handleCheckboxChange("useCbrVbr")} />{" "}
                        Type Bandwidth
                      </label>
                      {useCbrVbr && this.renderSelect("cbr_vbr", "", this.cbr_vbr_options)}
                    </div>
                  </Col>
                  <Col md="3">
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={useMotionDetection}
                          onChange={this.handleCheckboxChange("useMotionDetection")}
                        />{" "}
                        Motion Detection
                      </label>
                      {useMotionDetection && this.renderSelect("motion_detection", "", this.true_false_options)}
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md="3">
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={useEncryption}
                          onChange={this.handleCheckboxChange("useEncryption")}
                        />{" "}
                        Encryption (only HIK)
                      </label>
                      {useEncryption && this.renderSelect("encrypt", "", this.true_false_options)}
                    </div>
                  </Col>
                  <Col md="3">
                    {this.renderSelect(
                      "dhcp",
                      "DHCP (HIK/DAHUA only)",
                      this.props.server?.group_parent === "cloud" ? this.dhcp_options_cloud : this.dhcp_options
                    )}
                  </Col>
                </Row>
              </>
            )}

            <Row>
              <Col md="12">
                <Button
                  className="btn-link text-decoration-none"
                  color="link"
                  onClick={this.handleOtherParametersClick}
                >
                  {otherParametersButtonText}
                </Button>
              </Col>
            </Row>
          </div>
        )}
        <Button className="float-left btn-info mt-3 mr-2" onClick={this.handleInstallClick}>
          Install Update-dvr
        </Button>
        <Button className="float-right btn-success m-2" onClick={this.handleSubmit}>
          <i className="fas fa-search mr-2" />
          {typeActionLabel}
        </Button>
      </Form>
    );
  }
}

export default UpdatedvrForm;
